import React from "react";
import { Chip, ChipProps } from "@mui/material";
import { paletteVar, paletteVarAlpha } from "@platform/design-system";

const BRAND_TEAL = paletteVar("secondary-main", "#0078A2");

export type CustomColor =
  | "default"
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "error"
  | "warning";

type ColorStyles = {
  backgroundColor: string;
  color: string;
  borderColor?: string;
};

const customColors: Record<CustomColor, ColorStyles> = {
  default: {
    backgroundColor: "grey.100",
    borderColor: "grey.100",
    color: "text.secondary",
  },
  primary: {
    backgroundColor: paletteVarAlpha(BRAND_TEAL, 0.1),
    borderColor: paletteVarAlpha(BRAND_TEAL, 0.1),
    color: BRAND_TEAL,
  },
  secondary: {
    backgroundColor: "grey.100",
    borderColor: "grey.100",
    color: "text.main",
  },
  info: {
    backgroundColor: "grey.100",
    borderColor: "grey.100",
    color: "black",
  },
  success: {
    backgroundColor: "#4CAF501A",
    borderColor: "#4CAF501A",
    color: "#4CAF50",
  },
  error: {
    backgroundColor: "#F443361A",
    borderColor: "#F443361A",
    color: "#F44336",
  },
  warning: {
    backgroundColor: "#FF98001A",
    borderColor: "#FF98001A",
    color: "#FF9800",
  },
} as const;

type RoundedChipProps = Omit<ChipProps, "color"> & {
  color?: CustomColor;
};

export const RoundedChip = (props: RoundedChipProps): React.JSX.Element => {
  const { color, sx, ...rest } = props;

  let customColorStyles: Partial<{
    backgroundColor: string;
    color: string;
    borderColor: string;
  }> = {};

  if (color && color in customColors) {
    const styles = customColors[color];
    customColorStyles = {
      backgroundColor: styles.backgroundColor,
      color: styles.color,
      ...(styles.borderColor ? { borderColor: styles.borderColor } : {}),
    };
  }

  return (
    <Chip
      {...rest}
      sx={{
        borderRadius: "16px",
        fontWeight: 500,
        ...customColorStyles,
        ...sx,
      }}
    />
  );
};
